import { z } from "zod";
import {
  AccessResponse,
  CrashXError,
  EmailInput,
  ERROR_CODES,
  ResetPasswordInput,
  SuccessResponse,
  TokenInput,
} from "@crashx/shared";

async function request(path: string, options: RequestInit = {}): Promise<unknown> {
  const response = await fetch(`/api/v1/${path}`, {
    credentials: "same-origin",
    cache: "no-store",
    signal: AbortSignal.timeout(10000),
    ...options,
  });
  const value: unknown = await response.json();
  if (!response.ok) {
    const error = z.object({ error: z.object({ code: z.enum(ERROR_CODES) }) }).safeParse(value);
    throw new CrashXError(error.success ? error.data.error.code : "INTERNAL");
  }
  return value;
}

let csrfFlight: Promise<string> | null = null;

async function csrf(): Promise<string> {
  const cookie = document.cookie
    .split(";")
    .map((part) => part.trim())
    .find((part) => part.startsWith("crashx_csrf="));
  if (cookie) return decodeURIComponent(cookie.slice("crashx_csrf=".length));
  csrfFlight ??= request("auth/csrf")
    .then((value) => z.object({ csrfToken: z.string() }).parse(value).csrfToken)
    .finally(() => {
      csrfFlight = null;
    });
  return csrfFlight;
}

async function mutate(path: string, body: unknown): Promise<unknown> {
  const csrfToken = await csrf();
  return request(path, {
    method: "POST",
    headers: { "content-type": "application/json", "x-csrf-token": csrfToken },
    body: JSON.stringify(body),
  });
}

export const authApi = {
  access: async () => AccessResponse.parse(await request("me/access")),
  verifyEmail: async (token: string) => {
    SuccessResponse.parse(await mutate("auth/verify-email", TokenInput.parse({ token })));
  },
  resetPassword: async (input: unknown) => {
    SuccessResponse.parse(await mutate("auth/reset-password", ResetPasswordInput.parse(input)));
  },
  resendVerification: async (email: string) => {
    SuccessResponse.parse(await mutate("auth/resend-verification", EmailInput.parse({ email })));
  },
  requestPasswordReset: async (email: string) => {
    SuccessResponse.parse(await mutate("auth/forgot-password", EmailInput.parse({ email })));
  },
};

export type AuthActionFailure = "invalid" | "network";

export function classifyAuthActionFailure(error: unknown): AuthActionFailure {
  if (error instanceof CrashXError && (error.code === "UNAUTHENTICATED" || error.code === "VALIDATION")) {
    return "invalid";
  }
  return "network";
}
